import React, {
  Suspense,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useBackboneContext from "@src/backbone/provider/useBackboneContext";
import { useTranslation } from "@src/hooks/useTranslation";
import { useAppSelector } from "@src/store/hooks";
import { useDiscountNudgeVAE } from "@src/hooks/useDiscountNudgeVAE";
import { IAddressAutocompleteInstance } from "@src/components/widgets/components/PageHeaderWidget/blocks/AddressAutocomplete/AddressAutocompleteElement";
import { EAddressAutocompleteWrapper } from "@src/components/widgets/components/PageHeaderWidget/blocks/AddressAutocomplete/EAddressAutocompleteWrapper";

import StoreModel from "@src/backbone/model/StoreModel";
import {
  CartDeliveryOptions,
  EDeliveryOptionsMode,
  EFormatDeliveryDelay,
  ICartDeliveryOptionsVAD,
  ICartDeliveryOptionsVAE,
} from "@src/backbone/model/CartModel";
import DeliveryDelay from "design-system/src/Components/DrawerContent/DeliveryOptionsForm/DeliveryDelay/DeliveryDelay";
import moment from "moment";
import { useConfiguration } from "@src/hooks/query/useConfiguration";
import useAllStore from "@src/hooks/query/useAllStore";

import type { FeatureProperties } from "design-system/src/lib/MapBox/MapBox";
import { IDeliveryOptionsFormProps } from "design-system/src/Components/DrawerContent/DeliveryOptionsForm/DeliveryOptionsForm";
import { IPreorderOptions } from "@src/store/reducers/CartSlice/cart.interface";
import createModalRouteNavigation from "@src/components/general/ModalCheckoutRoute/createModalRouteNavigation";
import { cleanMySQLDateToISO } from "@src/utils/cleanDate";
import isUndefined from "lodash/isUndefined";
import { EAddressDisplayFormat } from "@src/backbone/model/AddressModel";
import SuspenseAfterSetup from "@src/lib/AfterSetup/SuspenseAfterSetup";
import {
  GATSBY_CONF_DIFFER_MAX_WEEKS,
  GATSBY_CONF_STORELOC_MAP_BOUNDS,
} from "@src/utils/constants";
import {
  isValidLngLat,
  parseLngLatBounds,
} from "design-system/src/lib/MapBox/mapBounds";
import useCartModelChange from "@src/backbone/hooks/useCartModelChange";
import TypographyLink from "@src/lib/rolling-start-navigation/TypographyLink";
import useResponsive from "@src/hooks/useResponsive";
import { EAddressAutocompleteResultType } from "@src/components/widgets/components/PageHeaderWidget/blocks/AddressAutocomplete/EAddressAutocompleteResultType";

const DeliveryOptionsFormDrawer = React.lazy(
  () =>
    import(
      "design-system/src/Components/DrawerContent/DeliveryOptionsForm/DeliveryOptionsFormDrawer"
    )
);
const DeliveryOptionsFormModal = React.lazy(
  () => import("./DeliveryOptionsModal/DeliveryOptionsModal")
);

export interface IDeliveryOptions {
  store?: StoreModel;
  vae: boolean;
  day?: string;
  time?: string;
  latitude?: number;
  longitude?: number;
  deliveryOptions?: CartDeliveryOptions;
}

export interface IDeliveryOptionBadgeProps {
  valid?: boolean;
  modeLabel: string;
  locationLabel: string;
  timeLabel: string;
  onClick?: () => void;
  wrapper?: "Navigation" | "Cart";
}

export enum EDeliveryOptionsUpdate {
  // FAILURE = 0,
  // NOOP = 1,
  UPDATE = 2,
  CREATE = 3,
}
export interface IDeliveryOptionsProps {
  wrapper?: "Navigation" | "Cart";
  drawerOnly?: boolean;
  drawerOpen?: boolean;
  disableAnimation?: boolean;
  drawerClassName?: string;
  onSuccess?: (status: EDeliveryOptionsUpdate) => void;
  onConfirm?: () => void;
  onCloseDrawer?: () => void;
  preorder?: IPreorderOptions;
  preset?: Partial<CartDeliveryOptions>;
  forceShowStatus?: boolean;
  isAddAddress?: boolean;
  fullScreen?: boolean;
}

export interface IDeliveryOptionsRef {
  open(): void;
  close(): void;
}

const AddressAutocompleteWrapper = React.lazy(
  () =>
    import(
      "@src/components/widgets/components/PageHeaderWidget/blocks/AddressAutocomplete/AddressAutocompleteWrapper"
    )
);
const DeliveryOptionsStatusBadge = React.lazy(
  () =>
    import(
      "design-system/src/Components/DeliveryOptionsStatus/DeliveryOptionsStatusBadge"
    )
);
const DeliveryOptionsStatus = React.lazy(
  () =>
    import(
      "design-system/src/Components/DeliveryOptionsStatus/DeliveryOptionsStatus"
    )
);
const NudgeVAEBadge = React.lazy(
  () => import("./NudgeVAEBadge")
);

const DeliveryOptions = React.forwardRef<
  IDeliveryOptionsRef,
  IDeliveryOptionsProps
>((props, ref) => {
  const {
    wrapper = "Navigation",
    drawerOnly,
    drawerOpen = false,
    preorder,
    preset: openWithPreset,
    onSuccess,
    onCloseDrawer,
    onConfirm: _onConfirm,
    forceShowStatus = false,
    isAddAddress = false,
    disableAnimation,
    drawerClassName,
    fullScreen,
  } = props;
  const { app } = useBackboneContext();
  const { isMobile } = useResponsive();
  const [, cart] = useCartModelChange("change");
  const isValid = cart.validateDeliveryOptions();
  const isVisible = useAppSelector(
    (state) => state.app.deliveryOptionsVisible && state.app.setupReady
  );
  const temporaryAddress = cart.getTemporaryAddress();
  const deliveryDelay = cart.formatDeliveryTimeLabel(EFormatDeliveryDelay.NAV);
  const deliveryType = cart.formatDeliveryTypeLabel(EFormatDeliveryDelay.NAV);
  const location = cart.formatLocation();
  const { t } = useTranslation(["Cart", "UI", "Datetime"]);
  const { getConfiguration } = useConfiguration();
  const { data: allStores } = useAllStore();
  const nudgeDiscount = useDiscountNudgeVAE();
  const title = t("Mes informations de livraison", "Cart");
  const fallbackBadgeLabel = t("Cagnottez 5% en Click & Collect", "Cart");
  const comeInBadgeLabel = (
    <Suspense fallback={fallbackBadgeLabel}>
      <NudgeVAEBadge discount={nudgeDiscount} fallbackLabel={fallbackBadgeLabel} />
    </Suspense>
  );
  const cancelLabel = t("Annuler", "UI");
  const confirmLabel = t("Confirmer", "UI");
  const dayPlaceholder = t("Choisir un jour", "Cart");
  const timePlaceholder = t("Choisir un créneau", "Cart");
  const deliveryDelayLabel = t("Créneau de livraison", "Cart");
  const seeStoreLabel = t("Voir la boutique", "Cart");
  const today = moment().format("YYYY-MM-DD");
  const [open, setOpen] = useState(drawerOpen);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<IDeliveryOptions>({ vae: false });
  const [preset, setPreset] = useState<CartDeliveryOptions | undefined>();
  const [delayPreset, setDelayPreset] = useState<{
    day?: string;
    time?: string;
  }>({});
  const [disabled, setDisabled] = useState(true);
  const addressAutocompleteRef = useRef<IAddressAutocompleteInstance>(null);
  const selectTimeRef = useRef<HTMLSelectElement>(null);

  useImperativeHandle(ref, () => {
    return {
      open: () => setOpen(true),
      close: () => setOpen(false),
    };
  });

  useEffect(() => {
    if (typeof drawerOpen === "boolean" && drawerOpen !== open) {
      setOpen(drawerOpen);
      if (drawerOpen) {
        openDrawer();
      }
    } else if (drawerOpen === true && open === true) {
      // refresh state
      openDrawer();
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  const storesGeoJson: GeoJSON.FeatureCollection<
    GeoJSON.Point,
    FeatureProperties
  > = {
    type: "FeatureCollection",
    features: allStores
      .filter((store) => {
        const lng = parseFloat(store.gmap_longitude as string);
        const lat = parseFloat(store.gmap_latitude as string);
        return isValidLngLat(lng, lat);
      })
      .map((store) => ({
        type: "Feature",
        properties: {
          name: store.name,
          id_store: store.id_store,
          shop_type: ["shop", "corner", "travel-retail"][
            store.type_shop as 0 | 1 | 2
          ] as FeatureProperties["shop_type"],
        },
        geometry: {
          type: "Point",
          coordinates: [
            parseFloat(store.gmap_longitude as string),
            parseFloat(store.gmap_latitude as string),
          ],
        },
      })),
  };

  const defaultMapBounds = parseLngLatBounds(
    getConfiguration("_STORELOC_MAP_BOUNDS_", GATSBY_CONF_STORELOC_MAP_BOUNDS),
    parseLngLatBounds(GATSBY_CONF_STORELOC_MAP_BOUNDS)
  );

  const mapboxProps: IDeliveryOptionsFormProps["mapboxProps"] = {
    accessToken: getConfiguration(
      "_MAPBOX_ACCESS_TOKEN_",
      "pk.eyJ1IjoiYWJlZ2FyaW4iLCJhIjoiY2ppYTM0ZGZhMDE4dTN2bDlvbmN3NDUwOSJ9.0Fc_gwOjTNrgO5ZX3CqqVg"
    ),
    mapStyle: getConfiguration(
      "_MAPBOX_STYLES_URL_",
      "mapbox://styles/abegarin/cjia052ic0d702rl6t6zgjbn5"
    ),
    storesGeoJson,
    defaultBounds: defaultMapBounds,
    onIconClick: ({ id_store, shop_type, name }) => {
      if (shop_type === "shop" && id_store) {
        addressAutocompleteRef.current?.select(name, {
          plain_text_label: name,
          grouped: false,
          value: id_store,
          label: name,
          data: {
            result_type: EAddressAutocompleteResultType.STORE,
            id_store: id_store,
            store_search_patterns: "",
            result_value: "",
            plain_text_label: name,
            description_primary: "",
            icon: "store",
          },
        });
      }
    },
  };

  const preorderRestrictions = useMemo(() => {
    if (preorder) {
      const { id_product } = preorder;
      const preorder_product = app.getProduct(id_product);
      if (preorder_product) {
        let minDate = moment().startOf("day");
        let maxDate = minDate.clone().add(GATSBY_CONF_DIFFER_MAX_WEEKS, "w");
        let product_visibility = preorder_product.getVisibility();
        let cartMomentDay: string;
        let cartDate = new Date();

        let from = preorder_product.get("from");
        let to = preorder_product.get("to");

        from = from ? cleanMySQLDateToISO(from) : false;
        to = to ? cleanMySQLDateToISO(to) : false;
        if (from) {
          from = moment(from).startOf("day");
          if (from.diff(minDate) > 0) {
            minDate = cartDate = from;
          }
        }
        if (to) {
          to = moment(to).startOf("day");
          if (maxDate.diff(to) > 0) {
            maxDate = to;
          }
        }

        cartMomentDay = moment(cartDate).format("YYYY-MM-DDT");

        const visibility = product_visibility
          .map((day_visibility) => {
            let dayFrom = moment(cartMomentDay + day_visibility.from),
              dayTo = moment(cartMomentDay + day_visibility.to),
              daySwitch = moment(cartMomentDay + "16:00:00"),
              dayDiff = dayTo.diff(dayFrom) / 60000, // in minute
              isWeekDayEnable = dayDiff >= 1,
              am = dayFrom.isBefore(daySwitch),
              pm = dayTo.isAfter(daySwitch);
            return {
              ...day_visibility,
              ...{
                enabled: isWeekDayEnable,
                am: am,
                pm: pm,
                hMin: dayFrom.hour(),
                mMin: dayFrom.minute(),
                hMax: dayTo.hour(),
                mMax: dayTo.minute(),
              },
            };
          })
          .sort((a, b) => (a?.day || 0) - (b?.day || 0));
        // sort by day, where 0 is sunday

        const preorderPreset = preorder?.preset;
        const vae = !!preorderPreset?.vae;

        const _options = {
          vae,
          deliveryOptions: preorderPreset,
          ...(vae && {
            id_store: preorderPreset?.id_store,
            autocomplete_literal: preorderPreset?.autocomplete_literal,
          }),
        };

        setPreset(preorderPreset);
        setOptions((prev) => ({ ...prev, ..._options }));

        return {
          visibility,
          minDate,
          maxDate,
        };
      }
    }
  }, [app, preorder]);

  const dayList = useMemo(() => {
    const _list: Array<{ label: string; value: any }> = [];

    Array.from(Array(GATSBY_CONF_DIFFER_MAX_WEEKS * 7).keys()).forEach(
      (dayDiff) => {
        const m = moment().add(dayDiff, "days");
        const day = m.day();
        const { visibility, minDate, maxDate } = { ...preorderRestrictions };
        if (minDate && m.isBefore(minDate)) return;
        if (maxDate && m.isAfter(maxDate)) return;
        const enabled: boolean = visibility
          ? visibility[day] && visibility[day].enabled
          : true;

        if (enabled) {
          const value = m.format("YYYY-MM-DD");
          const label = m.calendar({
            sameDay: t("[Aujourd'hui]", "Datetime"),
            nextDay: t("[Demain]", "Datetime"),
            nextWeek: t("dddd [prochain]", "Datetime"),
            sameElse: t("[le] Do MMMM", "Datetime"),
          });
          _list.push({
            label,
            value,
          });
        }
      }
    );

    return _list;
  }, [preorderRestrictions, t]);

  const getTimeList = (store?: StoreModel, isVAE = false, date?: Date) => {
    return store ? store.getTimesOptions(isVAE, date) : [];
  };
  const fromDate = (day?: string): Date => {
    return day ? new Date(day) : new Date();
  };

  const timeList = useMemo(() => {
    if (preorderRestrictions) {
      if (options.day) {
        // si une période est selectionnée, et que le store existe, on le set
        const times = options.store?.getTimes(
          options.vae,
          fromDate(options.day),
          false,
          true
        );

        const timesOptions: Array<{
          value: string;
          label: string;
          disabled: boolean;
        }> = [];
        const { visibility } = { ...preorderRestrictions };
        if (visibility && times) {
          const selectedDate = moment(fromDate(options.day));
          const dateDay = selectedDate.day();
          times.forEach((item) => {
            let _visibility = visibility[dateDay];
            const { h, m, isAvailable, label } = item,
              HH = `${h}`.padStart(2, "0"),
              MM = `${m}`.padStart(2, "0");
            if (_visibility) {
              const { hMax, mMax, hMin, mMin } = _visibility,
                is_before_visibility_max = h <= hMax && m <= mMax,
                is_after_visibility_min = h > hMin || (h === hMin && m >= mMin);

              if (
                isAvailable &&
                is_before_visibility_max &&
                is_after_visibility_min
              ) {
                timesOptions.push({
                  value: `${HH}:${MM}`,
                  label: label,
                  disabled: false,
                });
              }
            }
          });
        }
        return timesOptions;
      } else {
        return [];
      }
    }
    return getTimeList(options.store, options.vae, fromDate(options.day));
  }, [preorderRestrictions, options.store, options.vae, options.day]);

  const isFormValid = useMemo<boolean>(
    () => !!(options.store && options.day && options.time && !disabled),
    [options.store, options.day, options.time, disabled]
  );

  const onClose = () => {
    setOpen(false);
    setPreset(undefined);
    if (drawerOnly) {
      onCloseDrawer?.();
    }
    preorder?.onClose?.();
  };

  const openDrawer = (forceWithPreset = false) => {
    if (!open) {
      setOpen(true);
    }
    const withPreset = forceWithPreset || !isUndefined(openWithPreset?.vae);
    const currentDeliveryOptions = cart.getDeliveryOptions();
    const cartStore = cart.getStore();
    const cartVAE = cart.isVAE() && !!currentDeliveryOptions;
    const id_store = openWithPreset?.id_store;
    let store: StoreModel | undefined =
        !!id_store && withPreset ? app.getStore(id_store) : cartStore,
      vae: boolean = withPreset ? !!(openWithPreset?.vae ?? cartVAE) : cartVAE,
      day: string = cart.get("order_date") || today,
      time: string = cart.isNow()
        ? "now"
        : (cart.get("order_time") || "").replace(/(..:..).*/, "$1");
    let latitude: number | undefined, longitude: number | undefined;
    if (!cartStore) {
      day = today;
      time = "now";
    }
    let deliveryOptions: CartDeliveryOptions | undefined;
    if (preorder && preorder.delivery_mode_restrictions) {
      const delivery_mode_restrictions = preorder.delivery_mode_restrictions;
      if (
        vae &&
        delivery_mode_restrictions &&
        !delivery_mode_restrictions.includes(EDeliveryOptionsMode.VAE)
      ) {
        vae = false;
      } else if (
        !vae &&
        !!delivery_mode_restrictions &&
        !delivery_mode_restrictions.includes(EDeliveryOptionsMode.VAD)
      ) {
        vae = true;
      }
    } else if (withPreset) {
      if (vae) {
        deliveryOptions = {
          ...currentDeliveryOptions,
          vae: EDeliveryOptionsMode.VAE,
          ...(store && { id_store: store.id as string }),
        } as ICartDeliveryOptionsVAE;
        setPreset({
          ...deliveryOptions,
        } as ICartDeliveryOptionsVAE);
      } else {
        if (currentDeliveryOptions) deliveryOptions = currentDeliveryOptions;
        setPreset({
          ...deliveryOptions,
          vae: EDeliveryOptionsMode.VAD,
        } as ICartDeliveryOptionsVAD);
      }
    } else if (currentDeliveryOptions) {
      vae = !!currentDeliveryOptions.vae;
      deliveryOptions = currentDeliveryOptions;
      if (vae) {
        const deliveryOptionsVae =
          currentDeliveryOptions as ICartDeliveryOptionsVAE;
        const id_store = deliveryOptionsVae?.id_store;
        store = id_store ? app.getStore(id_store) : store;
        latitude = store?.getLatitude();
        longitude = store?.getLongitude();
      }
      setPreset(currentDeliveryOptions);
    }
    setDelayPreset(() => ({ day, time }));
    setOptions((prevState) => ({
      ...prevState,
      deliveryOptions,
      store,
      vae,
      day,
      time,
      latitude,
      longitude,
    }));
  };

  const onChangeDatetime = (day: string, time: string) => {
    setOptions((prev) => ({ ...prev, day, time }));
  };

  const onAutocompleteSuccess = (result: {
    store?: StoreModel;
    options: CartDeliveryOptions;
  }) => {
    if (isAddAddress) {
      onClose();
    } else {
      const { store, options } = result;
      let latitude: number | undefined, longitude: number | undefined;
      if (store) {
        latitude = store?.getLatitude();
        longitude = store?.getLongitude();
      }
      setDisabled(false);
      setOptions((prev) => ({
        ...prev,
        store,
        vae: !!options.vae,
        deliveryOptions: options,
        latitude,
        longitude,
      }));
    }
  };

  const onEmptyAddress = () => {
    setOptions((prev) => ({
      ...prev,
      latitude: undefined,
      longitude: undefined,
    }));
    setDisabled(true);
  };

  const onInvalidAddress = () => {
    onEmptyAddress();
  };

  const onConfirm = () => {
    if (options.deliveryOptions) {
      const { time, day } = options;
      const todayMoment = moment();
      const isToday = moment(day).isSame(todayMoment, "day");
      let order_time,
        now = isToday ? 0 : -1;
      if (time === "now") {
        now = 1;
        order_time = todayMoment.add(15).format("HH:mm:ss");
      } else {
        order_time = time + ":00";
      }
      const newDeliveryOptions = {
        ...options.deliveryOptions,
        now,
        order_date: day,
        order_time: order_time,
      };
      setLoading(true);
      const isPrevValid = cart.validateDeliveryOptions();
      const ApplyDeliveryOptions = () => {
        cart
          .setDeliveryOptions(newDeliveryOptions)
          .then(() => {
            setLoading(false);
            preorder?.onComplete?.(true);
            // Store redirect value before closing drawer to avoid stale reference
            const redirect = openWithPreset?.redirect;
            // Close drawer first to update Redux state
            onCloseDrawer?.();
            setOpen(false);
            _onConfirm?.();
            // Use setTimeout to ensure Redux state is updated before navigation
            // This prevents the modal from reopening after navigation
            if (redirect) {
              if (redirect === "close") {
                onClose();
              } else {
                setTimeout(() => {
                  app.go(redirect);
                }, 0);
              }
            }
          })
          .catch((reason: any) => {
            setLoading(false);
            preorder?.onComplete?.(false);
            if (!reason || !reason.cause?.silent) app.alert();
          })
          .finally(() => {
            setLoading(false);
            onSuccess?.(
              isPrevValid
                ? EDeliveryOptionsUpdate.UPDATE
                : EDeliveryOptionsUpdate.CREATE
            );
          });
      };
      // message d'avertissement avant de changer les critères de livraison
      if (isPrevValid && cart.hasSomeReductions()) {
        const warnTitle = t("Attention", "UI"),
          message = t(
            "En modifiant vos critères, certaines promotions de votre panier pourront ne plus être appliquées.",
            "Cart"
          );
        app.confirm({ title: warnTitle, subtitle: message }, (success) => {
          if (success) {
            ApplyDeliveryOptions();
          } else {
            setLoading(false);
          }
        });
      } else {
        ApplyDeliveryOptions();
      }
    }
  };

  useEffect(() => {
    // permet de d'appliquer la première valeur disponible dans la liste
    // si la valeur actuelle n'est pas dans la liste
    let time = options.time;
    let day: string | undefined;
    const haveCurrentTimeInList = !!timeList.find(
      (time) => time.value === options.time
    );
    if (options.day) day = options.day;
    if (!haveCurrentTimeInList) {
      time = timeList.find((time) => !time.disabled)?.value || "";
      setOptions((prev) => ({ ...prev, time }));
    }
    !!time &&
      setDelayPreset((prev) => {
        return { ...prev, time, day: day || prev.day };
      });
  }, [timeList]);

  const searchLabel = t("Saisissez votre adresse", "Cart");
  const onClickBadge = () => {
    openDrawer(true);
  };

  const deliveryStatusProps = {
    search: !isValid,
    timeLabel: deliveryDelay,
    locationLabel: location,
    modeLabel: deliveryType,
    searchLabel,
    onClick: onClickBadge,
  };

  const storeUrl = useMemo(() => {
    const id_store = options?.store?.getId();
    return id_store
      ? allStores.find((store) => store.id_store === id_store)?.uri
      : undefined;
  }, [options?.store, allStores]);

  const deliveryOpened = open || drawerOpen;

  const [displayAsModal, setDisplayAsModal] = useState(false);
  useEffect(() => {
    if (fullScreen && deliveryOpened && !isMobile) {
      setDisplayAsModal(true);
    } else if (!deliveryOpened || isMobile) {
      setDisplayAsModal(false);
    }
  }, [fullScreen, deliveryOpened, isMobile]);

  // render the address autocomplete
  const addressAutocompleteRender = (
    <Suspense>
      <AddressAutocompleteWrapper
        ref={addressAutocompleteRef}
        preset={preset}
        switcherValue={isAddAddress ? 0 : options.vae ? 1 : 0}
        isAddAddress={isAddAddress}
        wrapper={EAddressAutocompleteWrapper.DELIVERY_OPTIONS_FORM}
        onEmpty={onEmptyAddress}
        onInvalid={onInvalidAddress}
        onSuccess={onAutocompleteSuccess}
        deliveryModeRestrictions={preorder?.delivery_mode_restrictions}
        handleChangeMode={(vae: EDeliveryOptionsMode) => {
          setPreset((prev) => {
            if (typeof prev === "undefined") return;
            return { ...prev, vae };
          });
          setOptions((prev) => ({ ...prev, vae: !!vae }));
        }}
        inputTextValue={
          options.vae
            ? undefined
            : temporaryAddress?.formatFullAddress(
                EAddressDisplayFormat.AUTOCOMPLETE
              )
        }
        autoCompleteFooter={
          storeUrl &&
          options.vae && (
            <div style={{ height: 0, width: "100%", marginTop: 8 }}>
              <TypographyLink
                to={storeUrl}
                variant={"body2"}
                color={"champagne4"}
              >
                {seeStoreLabel}
              </TypographyLink>
            </div>
          )
        }
      />
    </Suspense>
  );

  // render the delivery delay
  const deliveryDelayRender = isAddAddress ? null : (
    <DeliveryDelay
      {...{
        selectTimeRef,
        preset: delayPreset,
        disabled,
        dayList,
        timeList,
        dayPlaceholder,
        timePlaceholder,
        onChange: onChangeDatetime,
      }}
      label={deliveryDelayLabel}
    />
  );

  // render the delivery options props
  const deliveryOptionsProps: IDeliveryOptionsFormProps = {
    extraContent: fullScreen ? undefined : createModalRouteNavigation(),
    title,
    comeInBadgeLabel,
    cancelLabel,
    confirmLabel,
    isFormValid,
    loading,
    onConfirm,
    onClose,
    options,
    open: deliveryOpened,
    vae: isAddAddress ? false : options.vae,
    mapboxProps,
    latitude: options.latitude,
    longitude: options.longitude,
    disableAnimation,
    drawerClassName,
  };

  return (
    <div className={"delivery-options"} style={{ maxWidth: "100%" }}>
      {drawerOnly ? null : wrapper === "Cart" ? (
        <Suspense>
          <DeliveryOptionsStatusBadge
            {...{
              ...deliveryStatusProps,
              modeLabel: deliveryType,
              wrapper,
            }}
          />
        </Suspense>
      ) : (
        <Suspense>
          <DeliveryOptionsStatus
            visible={forceShowStatus || isVisible}
            selectLabel={deliveryType}
            {...{
              ...deliveryStatusProps,
              style: { flex: 1 },
            }}
          />
        </Suspense>
      )}

      {displayAsModal ? (
        <SuspenseAfterSetup>
          <Suspense>
            <DeliveryOptionsFormModal
              {...{
                ...deliveryOptionsProps,
                extraContent: undefined,
              }}
              addressAutocomplete={addressAutocompleteRender}
              deliveryDelay={deliveryDelayRender}
            />
          </Suspense>
        </SuspenseAfterSetup>
      ) : (
        <SuspenseAfterSetup>
          <Suspense>
            <DeliveryOptionsFormDrawer
              {...deliveryOptionsProps}
              addressAutocomplete={addressAutocompleteRender}
              deliveryDelay={deliveryDelayRender}
            />
          </Suspense>
        </SuspenseAfterSetup>
      )}
    </div>
  );
});

export default React.memo(DeliveryOptions);
